import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { signInWithEmailAndPassword, sendPasswordResetEmail, signOut } from 'firebase/auth';
import { auth } from '../shared/config/firebase';
import { Mail, Lock, Package } from 'lucide-react';
import { Button, Input } from '../components/ui';
import InstallAppBanner from '../components/InstallAppBanner';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg, setForgotMsg] = useState<{type:'ok'|'err', text:string} | null>(null);

  const mainPlatform =
    window.location.hostname === 'sistemacourier.com' ||
    window.location.hostname === 'www.sistemacourier.com' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';

  // En la dirección de una empresa, "/" envía a cada rol a su inicio (courier, cliente,
  // agente, personal). En el dominio principal "/" es la página de presentación: ahí entra
  // sobre todo el súper administrador (si no lo es, SuperAdminRoute lo manda a /dashboard).
  const afterLogin = mainPlatform ? '/super-admin' : '/';

  // Fondo y botones con el color de la empresa; en el dominio principal, el rojo de la plataforma (Platform 2.0)
  const brandRaw = tenant?.branding?.primary_color;
  const brand = mainPlatform ? '#E50914' : (brandRaw && /^#[0-9a-f]{6}$/i.test(brandRaw) ? brandRaw : '#E50914');

  useEffect(() => {
    if (auth.currentUser) { navigate(afterLogin); }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);

      // La cuenta debe pertenecer a la empresa de esta dirección (el servidor igual
      // lo bloquea, pero el usuario veía pantallas vacías sin saber por qué).
      const { claims } = await cred.user.getIdTokenResult();
      const userTenant = typeof claims.tenantId === 'string' ? claims.tenantId : null;
      const realTenant = tenant?.id && !['generic-default', 'itobox-default'].includes(tenant.id); // sin servidor se usan empresas de respaldo
      if (userTenant && realTenant && userTenant !== tenant!.id && claims.role !== 'super_admin') {
        await signOut(auth);
        setError(`Esta cuenta no pertenece a ${tenant.branding?.company_name || tenant.name}. Entra desde la dirección de tu empresa.`);
        return;
      }

      navigate(afterLogin);
    } catch (error: any) {
      setError('Credenciales invalidas. Por favor, intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotMsg(null);
    try {
      await sendPasswordResetEmail(auth, forgotEmail);
      setForgotMsg({ type: 'ok', text: 'Te enviamos un link. Revisa tu bandeja (y spam).' });
    } catch (err: any) {
      setForgotMsg({ type: 'err', text: 'No encontramos ese correo. Verifica que sea el correcto.' });
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: `linear-gradient(160deg, ${brand} 0%, ${brand} 55%, ${brand}D9 100%)`, fontFamily: "Poppins, system-ui, sans-serif" }}>
      <div className="w-full max-w-md">

        {/* Logo/Brand */}
        <div className="text-center mb-8">
          {mainPlatform
            ? <div className="inline-flex items-center gap-3 bg-white rounded-2xl px-6 py-4 shadow-xl mb-5">
                <div className="flex items-center justify-center w-10 h-10 rounded-xl" style={{backgroundColor: brand}}>
                  <Package size={22} className="text-white" />
                </div>
                <span className="text-2xl font-bold" style={{color:'#111111'}}>Sistema Courier</span>
              </div>
            : tenant?.branding?.logo_url
              ? <div className="inline-flex items-center justify-center bg-white rounded-3xl px-6 py-4 shadow-xl mb-5">
                  <img src={tenant.branding.logo_url} alt={tenant.branding.company_name || 'Logo'} className="h-28 w-auto object-contain" />
                </div>
              : <div>
                <div className="inline-flex items-center justify-center w-20 h-20 bg-white/15 rounded-2xl mb-5 shadow-xl">
                  <Package size={40} className="text-white" />
                </div>
                <h1 className="text-3xl font-bold text-white mb-2 drop-shadow-md">
                  {tenant?.branding?.company_name || 'Mi Empresa'}
                </h1>
              </div>
          }
          <p className="text-white/90 text-base">Ingresa a tu cuenta</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-3xl p-10 shadow-2xl mb-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <Input label="Correo electrónico" id="email" type="email" value={email}
              onChange={(e) => setEmail(e.target.value)} required placeholder="tu@email.com"
              leftIcon={<Mail size={20} />} className="w-full" />
            <Input label="Contraseña" id="password" type="password" value={password}
              onChange={(e) => setPassword(e.target.value)} required placeholder="..."
              leftIcon={<Lock size={20} />} className="w-full" />
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3.5">
                <p className="text-red-700 text-sm font-medium m-0">{error}</p>
              </div>
            )}
            <div className="text-right -mt-2">
              <button type="button" onClick={() => { setShowForgot(true); setForgotEmail(email); setForgotMsg(null); }}
                className="text-sm hover:underline font-medium bg-transparent border-none cursor-pointer p-0" style={{ color: brand }}>
                ¿Olvidaste tu contraseña?
              </button>
            </div>
            <Button type="submit" disabled={loading}
              className="w-full justify-center py-3.5 text-base font-semibold hover:shadow-lg hover:translate-y-[-1px] transition-all" style={{ background: brand }}>
              {loading ? 'Iniciando sesión...' : 'Iniciar sesión'}
            </Button>
            {/* En el dominio principal se registran empresas; en el de cada empresa, sus clientes */}
            <p className="text-center text-sm text-gray-500 mt-5">
              {mainPlatform ? '¿Tu empresa aún no usa Sistema Courier?' : '¿Eres cliente nuevo?'}{' '}
              <a href={mainPlatform ? '/register' : '/client/register'} className="font-semibold hover:underline" style={{ color: brand }}>
                {mainPlatform ? 'Prueba gratis' : 'Regístrate aquí'}
              </a>
            </p>
          </form>
        </div>

        {/* Invitación a instalar la app (solo en el celular y en la dirección de una empresa) */}
        {!mainPlatform && <InstallAppBanner variant="light" className="mb-6" />}

        {/* Modal: Olvide mi contrasena */}
        {showForgot && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:'16px' }}>
            <div style={{ background:'white', borderRadius:'20px', padding:'32px', width:'100%', maxWidth:'400px', boxShadow:'0 25px 50px rgba(0,0,0,.3)' }}>
              <h2 style={{ margin:'0 0 8px', fontSize:'20px', fontWeight:'800', color:'#1e293b' }}>Recuperar contraseña</h2>
              <p style={{ margin:'0 0 20px', fontSize:'14px', color:'#64748b' }}>
                Ingresa tu correo y te enviamos un link para crear tu contraseña.
              </p>
              <form onSubmit={handleForgotPassword}>
                <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)}
                  placeholder="tu@correo.com" required
                  style={{ width:'100%', padding:'12px 16px', border:'1.5px solid #e2e8f0', borderRadius:'10px', fontSize:'15px', marginBottom:'12px', boxSizing:'border-box', outline:'none' }}
                />
                {forgotMsg && (
                  <div style={{
                    padding:'12px', borderRadius:'10px', marginBottom:'12px', fontSize:'13px',
                    background: forgotMsg.type === 'ok' ? '#f0fdf4' : '#fef2f2',
                    color: forgotMsg.type === 'ok' ? '#166534' : '#dc2626',
                    border: forgotMsg.type === 'ok' ? '1px solid #bbf7d0' : '1px solid #fecaca',
                  }}>
                    {forgotMsg.text}
                  </div>
                )}
                <div style={{ display:'flex', gap:'10px' }}>
                  <button type="button" onClick={() => setShowForgot(false)}
                    style={{ flex:1, padding:'12px', background:'#f1f5f9', border:'none', borderRadius:'10px', fontSize:'14px', fontWeight:'600', cursor:'pointer', color:'#475569' }}>
                    Cancelar
                  </button>
                  <button type="submit" disabled={forgotLoading}
                    style={{ flex:2, padding:'12px', background: brand, border:'none', borderRadius:'10px', fontSize:'14px', fontWeight:'700', cursor:'pointer', color:'white', opacity: forgotLoading ? 0.7 : 1 }}>
                    {forgotLoading ? 'Enviando...' : 'Enviar link'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-white/80">
          Copyright {new Date().getFullYear()} {mainPlatform ? 'Sistema Courier' : (tenant?.branding?.company_name || 'Mi Empresa')}. Todos los derechos reservados.
        </p>

      </div>
    </div>
  );
};

export default LoginPage;
